import React, {useState, useEffect} from "react";
import {DatePicker, Tooltip, Button, Select} from "antd";
import moment from "moment";
import {useTranslation} from "react-i18next";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {datePeriodOptions} from "../../../constants/constants";
import {StyledRangePickerContainer, FilterLabel, DatePickerWrapper} from "./FilterComponentStyles";

const DatePickerForReportPage = ({
    startDate,
    endDate,
    period,
    setStartDate,
    setEndDate,
    setPeriod,
    disabledDate,
    showPrevButton = false,
    showNextButton = false,
    showYesterday = true,
    showAllTime = false,
    onDatePickerExpandedChange
}) => {
    const {t} = useTranslation(["datePeriodOptions"]);
    const [dependencyCondition, setDependencyCondition] = useState(period === "choose");
    const [dates, setDates] = useState(
        startDate && endDate ? [moment(startDate), moment(endDate)] : [moment(), moment()]
    );

    useEffect(() => {
        setDependencyCondition(period === "choose");
    }, [period]);

    useEffect(() => {
        if (onDatePickerExpandedChange) {
            onDatePickerExpandedChange(dependencyCondition);
        }
    }, [dependencyCondition, onDatePickerExpandedChange]);

    const onChange = value => {
        if (value === "choose") {
            setStartDate(
                moment()
                    .subtract(1, "days")
                    .format("YYYY-MM-DD")
            );
            setEndDate(moment().format("YYYY-MM-DD"));
            setPeriod(value);
            setDependencyCondition(true);
        } else if (value === "today") {
            setStartDate(moment().format("YYYY-MM-DD"));
            setEndDate(moment().format("YYYY-MM-DD"));
            setPeriod(value);
            setDependencyCondition(false);
        } else if (value === "yesterday") {
            setStartDate(
                moment()
                    .subtract(1, "days")
                    .format("YYYY-MM-DD")
            );
            setEndDate(
                moment()
                    .subtract(1, "days")
                    .format("YYYY-MM-DD")
            );
            setPeriod(value);
            setDependencyCondition(false);
        } else if (value === "tomorrow") {
            setStartDate(
                moment()
                    .add(1, "days")
                    .format("YYYY-MM-DD")
            );
            setEndDate(
                moment()
                    .add(1, "days")
                    .format("YYYY-MM-DD")
            );
            setPeriod(value);
            setDependencyCondition(false);
        } else {
            setStartDate("");
            setEndDate("");
            setPeriod(value);
            setDependencyCondition(false);
        }
    };

    const nextDay = () => {
        setDates([moment(dates[0]).add(1, "days"), moment(dates[1]).add(1, "days")]);
        setStartDate(dates[0].add(1, "days").format("YYYY-MM-DD"));
        setEndDate(dates[1].add(1, "days").format("YYYY-MM-DD"));
    };

    const previousDay = () => {
        setDates([moment(dates[0]).subtract(1, "days"), moment(dates[1]).subtract(1, "days")]);
        setStartDate(dates[0].subtract(1, "days").format("YYYY-MM-DD"));
        setEndDate(dates[1].subtract(1, "days").format("YYYY-MM-DD"));
    };

    const filterByDateOptions = datePeriodOptions.filter(option => {
        if (!showYesterday && option.value === "yesterday") return false;
        if (!showNextButton && option.value === "tomorrow") return false;
        if (!showPrevButton && option.value === "today") return false;
        if (!showAllTime && option.value === "") return false;
        return true;
    });

    return (
        <div style={{minWidth: "200px", maxWidth: "270px", marginRight: "16px"}}>
            <FilterLabel>{t("fields.date", {ns: "common"})}</FilterLabel>
            <DatePickerWrapper>
                <Select
                    data-testid="timeRangeSelect"
                    value={period}
                    onChange={onChange}
                    size="middle"
                    style={{flex: "1", minWidth: "120px"}}
                >
                    {filterByDateOptions.map((obj, i) => (
                        <Select.Option
                            key={i}
                            data-testid="timeRangeSelect_option"
                            value={obj.value}
                        >
                            {t(obj.translation_key, {
                                ns: "datePeriodOptions"
                            })}{" "}
                        </Select.Option>
                    ))}
                </Select>
                {dependencyCondition && (
                    <>
                        {dependencyCondition && showPrevButton && (
                            <Tooltip
                                title={t("prevDay", {
                                    ns: "datePeriodOptions"
                                })}
                            >
                                <Button
                                    data-testid="prevDayIcon"
                                    onClick={() => previousDay()}
                                    icon={<LeftOutlined />}
                                    size="middle"
                                ></Button>
                            </Tooltip>
                        )}
                        <DatePicker.RangePicker
                            panelRender={panelNode => (
                                <StyledRangePickerContainer>{panelNode}</StyledRangePickerContainer>
                            )}
                            allowClear={false}
                            value={dates || value}
                            data-testid="timeRangePicker"
                            size="middle"
                            renderExtraFooter={() => {
                                t("timeRangePickerPlaceholder", {ns: "common"});
                            }}
                            placeholder={[
                                t("fields.startDate", {ns: "common"}),
                                t("fields.endDate", {ns: "common"})
                            ]}
                            defaultValue={
                                startDate && endDate
                                    ? [moment(startDate), moment(endDate)]
                                    : undefined
                            }
                            onChange={(_, formatString) => {
                                setStartDate(formatString[0]);
                                setEndDate(formatString[1]);
                            }}
                            onCalendarChange={val => setDates(val)}
                            style={{flex: "3", minWidth: "270px"}}
                        />
                        {dependencyCondition && showNextButton && (
                            <Tooltip
                                title={t("nextDay", {
                                    ns: "datePeriodOptions"
                                })}
                            >
                                <Button
                                    data-testid="nextDayIcon"
                                    onClick={() => nextDay()}
                                    icon={<RightOutlined />}
                                    size="small"
                                ></Button>
                            </Tooltip>
                        )}
                    </>
                )}
            </DatePickerWrapper>
        </div>
    );
};

export default DatePickerForReportPage;
